import React from 'react';
import { Home, Search, FileText, Users, TrendingUp, Clock, CheckCircle, ArrowRight, Building2, MessageCircle, Star, Target } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const CorretoresPage = () => {
  const features = [
    {
      icon: <Search className="h-8 w-8 text-green-600" />,
      title: "Busca Inteligente de Oportunidades",
      description: "Encontre os melhores imóveis em leilão com filtros avançados e alertas personalizados.",
      benefits: ["Filtros por ROI", "Alertas automáticos", "Base atualizada diariamente"]
    },
    {
      icon: <FileText className="h-8 w-8 text-green-600" />,
      title: "Relatórios Profissionais",
      description: "Gere relatórios detalhados de viabilidade que impressionam e convencem seus clientes.",
      benefits: ["Design profissional", "Análise completa", "Marca personalizada"]
    },
    {
      icon: <Users className="h-8 w-8 text-green-600" />,
      title: "CRM para Leilões",
      description: "Gerencie seus leads interessados em leilões com ferramentas especializadas.",
      benefits: ["Pipeline de vendas", "Follow-up automático", "Histórico completo"]
    },
    {
      icon: <Target className="h-8 w-8 text-green-600" />,
      title: "Análise de Mercado",
      description: "Acesse dados de valorização e tendências para orientar seus clientes com precisão.",
      benefits: ["Dados históricos", "Projeções futuras", "Comparativos regionais"]
    }
  ];

  const strategies = [
    {
      title: "Especialização em Leilões",
      description: "Torne-se o corretor de referência em leilões da sua região",
      commission: "6-12%",
      volume: "Alto"
    },
    {
      title: "Consultoria para Investidores",
      description: "Ofereça análises profissionais para investidores sérios",
      commission: "3-6%",
      volume: "Médio"
    },
    {
      title: "Parcerias com Correspondentes",
      description: "Trabalhe junto com correspondentes em financiamentos",
      commission: "2-4%",
      volume: "Alto"
    }
  ];

  const testimonials = [
    {
      name: "Marina Santos",
      role: "Corretora - Campinas/SP",
      content: "Em 8 meses me tornei a especialista em leilões da região. Minha renda triplicou!",
      rating: 5,
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg"
    },
    {
      name: "Roberto Lima",
      role: "Corretor - Ribeirão Preto/SP",
      content: "O ImóvelIA me deu credibilidade. Meus clientes confiam nas minhas análises.",
      rating: 5,
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-green-100 font-medium mb-6">
              <Home className="h-5 w-5" />
              <span>Especializado para Corretores Imobiliários</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Torne-se o Especialista em Leilões da sua Região
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8">
              Multiplique sua renda especializando-se no nicho mais lucrativo do mercado imobiliário
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5516999631188?text=Olá! Sou corretor e quero me especializar em leilões"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-green-600 hover:bg-green-50 px-8 py-4 rounded-lg font-medium transition-colors text-lg"
              >
                <MessageCircle className="h-5 w-5" />
                Falar com Especialista
              </a>
              <a
                href="https://pay.kiwify.com.br/cSvs3ux"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-lg font-medium transition-colors text-lg"
              >
                <Home className="h-5 w-5" />
                Começar Especialização
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Auctions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Por que Leilões são o Futuro?
            </h2>
            <p className="text-xl text-slate-600">
              Descubra por que corretores especializados em leilões ganham 3x mais
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Comissões Maiores</h3>
              <p className="text-slate-600">Leilões oferecem comissões de 6-12%, até 3x maiores que vendas tradicionais</p>
            </div>
            <div className="text-center p-6">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Clientes Qualificados</h3>
              <p className="text-slate-600">Investidores sérios com capital disponível e decisão rápida</p>
            </div>
            <div className="text-center p-6">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Menos Concorrência</h3>
              <p className="text-slate-600">Poucos corretores dominam este nicho especializado e lucrativo</p>
            </div>
          </div>

          <div className="bg-green-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-center mb-8">Estratégias de Monetização</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {strategies.map((strategy, index) => (
                <div key={index} className="bg-white p-6 rounded-lg">
                  <h4 className="font-semibold text-lg mb-2">{strategy.title}</h4>
                  <p className="text-slate-600 text-sm mb-4">{strategy.description}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 font-medium">Comissão: {strategy.commission}</span>
                    <span className="text-slate-500">Volume: {strategy.volume}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Ferramentas Profissionais para Corretores
            </h2>
            <p className="text-xl text-slate-600">
              Tudo que você precisa para se destacar no mercado de leilões
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-green-100 p-3 rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Resultados dos Nossos Corretores
            </h2>
            <p className="text-xl text-slate-600">
              Veja como o ImóvelIA está transformando carreiras
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">R$ 45k</div>
              <div className="text-slate-600">Comissão média mensal</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">60%</div>
              <div className="text-slate-600">Mais leads qualificados</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">3x</div>
              <div className="text-slate-600">Aumento na renda</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">89%</div>
              <div className="text-slate-600">Taxa de fechamento</div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-green-50 rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-sm text-slate-600">{testimonial.role}</p>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                </div>
                <p className="text-slate-700 italic">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Triplicar sua Renda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Junte-se aos corretores que já descobriram o poder dos leilões para multiplicar suas comissões
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5516999631188?text=Olá! Quero me tornar especialista em leilões. Podem me ajudar?"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-green-600 hover:bg-green-50 px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <Users className="h-5 w-5" />
              Falar com Mentor
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href="https://pay.kiwify.com.br/cSvs3ux"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <Home className="h-5 w-5" />
              Começar Agora - R$ 79,90/mês
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CorretoresPage;